/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useState } from 'react';
import Downshift, { PropGetters } from 'downshift';
import {
  Popper,
  Paper,
  MenuList,
  MenuItem,
  Typography,
} from '@material-ui/core';
import styles from './styles';

interface InjectedProps<Item> {
  getInputProps: PropGetters<Item>['getInputProps'];
  getLabelProps: PropGetters<Item>['getLabelProps'];
  inputRef: (input: HTMLInputElement) => void;
  getRootProps: PropGetters<Item>['getRootProps'];
}

interface PropTypes<Item> {
  id?: string;
  suggestions: Item[];
  itemToString: (item: Item | null) => string;
  onSelect?: (item: Item) => unknown;
  children: (props: InjectedProps<Item>) => React.ReactElement;
  enabled?: boolean;
  inputValue?: string | null;
}

function SuggestionItem<Item>({
  getItemProps,
  item,
  itemToString,
  highlight,
}: {
  getItemProps: PropGetters<Item>['getItemProps'];
  item: Item;
  itemToString: PropTypes<Item>['itemToString'];
  highlight: boolean;
}): React.ReactElement {
  const props = getItemProps({
    item,
    style: {
      backgroundColor: highlight ? 'lightgray' : 'white',
    },
  });
  return (
    <MenuItem {...props}>
      <Typography variant="inherit" noWrap>
        {itemToString(item)}
      </Typography>
    </MenuItem>
  );
}

function Autocomplete<ItemType>({
  id,
  suggestions,
  itemToString,
  onSelect,
  children,
  inputValue,
  enabled = true,
}: PropTypes<ItemType>): React.ReactElement {
  const [childNode, setChildNode] = useState<HTMLInputElement>();

  return (
    <Downshift
      id={id}
      itemToString={itemToString}
      onSelect={onSelect}
      inputValue={inputValue || ''}
    >
      {downshift => {
        const {
          getInputProps,
          getItemProps,
          getLabelProps,
          getMenuProps,
          getRootProps,
          isOpen,
          highlightedIndex,
        } = downshift;

        const menuProps = isOpen
          ? getMenuProps({}, { suppressRefError: true })
          : {};

        const child = children({
          getInputProps,
          getLabelProps,
          inputRef: setChildNode,
          getRootProps,
        });

        const menuWidth = childNode ? childNode.clientWidth + 2 : 0;

        return (
          <div>
            {child}
            {childNode && (
              <Popper
                open={enabled && isOpen && suggestions.length > 0}
                anchorEl={childNode}
                css={styles.popper}
                placement="bottom"
              >
                <Paper css={{ width: menuWidth }}>
                  <MenuList {...menuProps}>
                    {suggestions.map((item, i) => {
                      return (
                        <SuggestionItem
                          key={i}
                          getItemProps={getItemProps}
                          item={item}
                          itemToString={itemToString}
                          highlight={highlightedIndex === i}
                        />
                      );
                    })}
                  </MenuList>
                </Paper>
              </Popper>
            )}
          </div>
        );
      }}
    </Downshift>
  );
}

export default Autocomplete;
